import { useQuery } from "@apollo/client";
import { Launch as DetailsIcon, DoNotDisturb } from "@mui/icons-material";
import { Button, Typography } from "@mui/material";
import { keyBy, last } from "lodash";
import { ReactElement, useEffect, useState } from "react";
import {
  useQueryParam,
  withDefault,
  NumberParam,
  StringParam,
} from "use-query-params";

import { TargetingForm, TargetingFormSchema } from "@stw/yup-validators";

import {
  GetAudienceSearchUserFilterQuery,
  AudienceSearchFilterType,
  AudienceFieldData,
  AudienceSearchResult,
  GetAudienceContactFieldsQuery,
  ListAudienceSearchResultsQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Table, {
  TableControls,
  TableColumnAlignment,
  RowAction,
} from "~frontend/components/Table";
import { DEFAULT_TARGET } from "~frontend/components/audienceFilteringForm";
import { LIST_AUDIENCE_SEARCH_RESULTS } from "~frontend/graphql/audience";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { Audience } from "~frontend/routes/subaccounts/audiences/Subscribers/types";
import { palette } from "~frontend/theme";
import { formatShortDateTime } from "~frontend/util/dateFormat";
import { useContactNameFormatter } from "~frontend/util/formatContactName";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";

import AudienceContactDialog from "./AudienceContactDialog";
import { AudienceFilterForm } from "./AudienceFilterForm";
import { SavedSearches, SavedSearchResultSummary } from "./SavedSearches";
import { SMALL_BUTTON_STYLE } from "./constants";
import { useAudience } from "./store";

export type SearchResultWithContact = {
  id: string;
  audienceContactId: string;
  phoneNumber: string;
  fields: AudienceFieldData[];
  createdAt: number;
  updatedAt: number;
  optedIn: boolean;
};

export function AudienceSubscribersTable({
  subscriberAudience,
  userSearchFilter,
  fieldDefinitions,
}: {
  subscriberAudience: Audience;
  userSearchFilter?: GetAudienceSearchUserFilterQuery["getAudienceSearchUserFilter"];
  fieldDefinitions: GetAudienceContactFieldsQuery["getAudienceContactFields"];
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const formatSubscriberName = useContactNameFormatter();
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  const currentFilter = useAudience(store => store.currentFilter);
  const setRefetchSearchResults = useAudience(
    store => store.setRefetchSearchResults
  );
  const clearFilters = useAudience(store => store.clearFilters);

  const [dialogOpenFor, setDialogOpenFor] =
    useState<SearchResultWithContact | null>(null);
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, "CREATED_AT_DESC")
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const [activeStatusFilter, setActiveStatusFilter] = useQueryParam(
    "active_status_filter",
    withDefault(StringParam, "active")
  );

  const startId = cursorStack[page] || null;
  const { data, loading, error, refetch } =
    useQuery<ListAudienceSearchResultsQuery>(LIST_AUDIENCE_SEARCH_RESULTS, {
      variables: {
        input: {
          subaccountId: subaccount.id,
          audienceId: subscriberAudience.id,
          audienceSearchFilterId: currentFilter?.id,
          activeStatusFilter:
            activeStatusFilter === "all"
              ? null
              : activeStatusFilter === "active",
          search: search,
          limit: pageSize,
          startId,
          sort,
        },
      },
    });

  useEffect(() => {
    setRefetchSearchResults(refetch);
  }, [refetch, setRefetchSearchResults]);

  useEffect(() => {
    clearFilters();
  }, [clearFilters]);

  function handlePageChange(newPage: number) {
    const isGoingForward = newPage > page;

    if (isGoingForward) {
      const lastRow = last(
        data?.listAudienceSearchResults.audienceSearchResults
      ) as AudienceSearchResult;

      if (lastRow) {
        setCursorStack(prev => {
          const newStack = [...prev];
          newStack[newPage] = lastRow.id;
          return newStack;
        });
      }
    } else {
      setCursorStack(prev => prev.slice(0, newPage + 1));
    }

    setPage(newPage);
  }

  useEffect(() => {
    setPage(0);
    setCursorStack([]);
    void refetch();
  }, [currentFilter, activeStatusFilter, refetch, setPage, setCursorStack]);

  const setFilter = useAudience(store => store.setFilter);
  const isFilterEmpty = useAudience(store => store.isFilterEmpty);
  const isEditingExistingFilter = useAudience(
    store => store.isEditingExistingFilter
  );
  const setIsEditingExistingFilter = useAudience(
    store => store.setIsEditingExistingFilter
  );

  const searchJobIsRunning = useAudience(store => store.searchJobIsRunning);

  const showFilterContactsButton =
    currentFilter.type === AudienceSearchFilterType.SavedSearch ||
    subscriberAudience.archivedAt
      ? false
      : !isEditingExistingFilter && isFilterEmpty();

  const sorts = [
    {
      value: "CREATED_AT_DESC",
      label: "Date Added, Newest First",
    },
    {
      value: "CREATED_AT_ASC",
      label: "Date Added, Oldest First",
    },
  ];

  return (
    <>
      <TableControls
        variant="compact"
        filters={[
          {
            id: "sort-by",
            label: "Sort By",
            onChange: (newSortBy: string) => {
              setSort(newSortBy);
            },
            options: sorts,
            value: sort,
          },
          {
            id: "active-status",
            label: "Subscription Status",
            onChange: (newStatusFilter: string) => {
              setActiveStatusFilter(newStatusFilter);
            },
            options: [
              {
                value: "all",
                label: "All",
              },
              {
                value: "active",
                label: "Subscribed",
              },
              {
                value: "unsubscribed",
                label: "Unsubscribed",
              },
            ],
            value:
              activeStatusFilter === "active"
                ? "active"
                : activeStatusFilter === "unsubscribed"
                  ? "unsubscribed"
                  : "all",
          },
        ]}
        search={{
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: "Search by name or phone number",
          value: search,
        }}
        controlsFooter={
          showFilterContactsButton && (
            <FlexBox justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                sx={SMALL_BUTTON_STYLE}
                onClick={() => {
                  const targetGroups = userSearchFilter
                    ? TargetingFormSchema.parse(userSearchFilter.searchBody)
                        .targetGroups
                    : ([
                        DEFAULT_TARGET,
                      ] as unknown as TargetingForm["targetGroups"]);

                  setFilter({
                    targetGroups,
                    type: AudienceSearchFilterType.UserSearch,
                    id: userSearchFilter?.id,
                    name: "",
                  });
                  setIsEditingExistingFilter(true);
                }}
              >
                Filter Contacts
              </Button>
            </FlexBox>
          )
        }
      />

      <FlexBox
        direction="column"
        sx={{ mt: -4, borderTop: `1px solid ${palette.lightestGray}` }}
      >
        {isEditingExistingFilter ? (
          <AudienceFilterForm
            audienceId={subscriberAudience.id}
            isOptInAudience={!!subscriberAudience.shortCodeConfig}
          />
        ) : (
          !isFilterEmpty() && (
            <SavedSearchResultSummary audienceId={subscriberAudience.id} />
          )
        )}

        {!subscriberAudience.archivedAt && (
          <SavedSearches audienceId={subscriberAudience.id} />
        )}

        {searchJobIsRunning ? (
          <Loading />
        ) : (
          <AsyncWrapper loading={loading} errors={error} data={data}>
            {searchResults => {
              if (searchResults.listAudienceSearchResults.totalCount === 0) {
                return (
                  <FlexBox
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    sx={{ py: 10, px: 5, backgroundColor: palette.white }}
                  >
                    <Typography variant="body2">
                      No contacts met your filter criteria.
                    </Typography>
                    <LinkButton onClick={() => setSearch("")}>
                      <Typography variant="body2">Clear filters</Typography>
                    </LinkButton>
                  </FlexBox>
                );
              }

              return (
                <FlexBox sx={{ px: 5, py: 3, backgroundColor: palette.white }}>
                  <Table
                    containerSx={{
                      "& .MuiTableHead-root": {
                        "& .MuiTableCell-root": {
                          fontWeight: 600,
                        },
                      },
                    }}
                    totalCount={
                      searchResults.listAudienceSearchResults.totalCount
                    }
                    rows={
                      searchResults.listAudienceSearchResults
                        .audienceSearchResults
                    }
                    columns={[
                      {
                        key: "phoneNumber",
                        title: "Number",
                        align: "left",
                        sx: { verticalAlign: "top" },
                        renderValue: (
                          searchResult: SearchResultWithContact
                        ) => {
                          const displayNumber = searchResult.phoneNumber
                            ? prettyPhoneNumber(searchResult.phoneNumber)
                            : "";

                          return searchResult.optedIn ? (
                            displayNumber
                          ) : (
                            <>
                              {displayNumber}
                              <br />
                              <Typography
                                sx={{
                                  fontStyle: "italic",
                                  display: "flex",
                                  alignItems: "center",
                                  mt: 0.5,
                                }}
                                variant="body3"
                              >
                                <DoNotDisturb
                                  sx={{
                                    width: "10px",
                                    height: "10px",
                                    mr: 0.5,
                                  }}
                                />
                                Opted Out
                              </Typography>
                            </>
                          );
                        },
                      },
                      {
                        key: "name",
                        title: "Name",
                        align: "left",
                        sx: { verticalAlign: "top" },
                        renderValue: (searchResult: SearchResultWithContact) =>
                          formatSubscriberName(
                            {
                              firstName: searchResult.fields?.find(
                                f => f.name === "first_name"
                              )?.value,
                              lastName: searchResult.fields?.find(
                                f => f.name === "last_name"
                              )?.value,
                            },
                            {
                              overrideNoNameText: "-",
                            }
                          ),
                      },
                      {
                        key: "address",
                        title: "Address",
                        align: "left",
                        sx: { verticalAlign: "top" },
                        renderValue: searchResult => {
                          const fields =
                            keyBy(searchResult.fields, "name") || {};
                          return (
                            <>
                              {fields.address_line_1?.value}
                              {fields.address_line_1?.value &&
                                fields.address_line_2?.value &&
                                ", "}
                              {fields.address_line_2?.value}
                              {(fields.address_line_1?.value ||
                                fields.address_line_2?.value) &&
                                (fields.city?.value ||
                                  fields.state?.value ||
                                  fields.zip?.value) && <br />}
                              {[fields.city?.value, fields.state?.value]
                                .filter(Boolean)
                                .join(", ")}{" "}
                              {fields.zip?.value}
                            </>
                          );
                        },
                      },
                      {
                        key: "added",
                        title: "Added",
                        align: "left",
                        sx: { verticalAlign: "top" },
                        renderValue: searchResult => {
                          const formattedCreatedAt = formatShortDateTime(
                            searchResult.createdAt
                          );
                          const formattedUpdatedAt = formatShortDateTime(
                            searchResult.updatedAt
                          );

                          return (
                            <>
                              {formattedCreatedAt}
                              {formattedUpdatedAt !== formattedCreatedAt && (
                                <Typography display="inline" variant="body3">
                                  <br />
                                  Updated {formattedUpdatedAt}
                                </Typography>
                              )}
                            </>
                          );
                        },
                      },
                      ...(customFieldsAllowed
                        ? [
                            {
                              key: "customFields",
                              title: "Custom Fields",
                              align: "left" as TableColumnAlignment,
                              sx: { maxWidth: "450px", verticalAlign: "top" },
                              renderValue: (
                                searchResult: SearchResultWithContact
                              ) => {
                                // only built-in fields have displayName
                                const customFields = (
                                  searchResult.fields || []
                                ).filter(f => !f.displayName);
                                if (customFields.length === 0) {
                                  return <div />;
                                }
                                return customFields.map(field => (
                                  <div key={field.name}>
                                    {field.name}
                                    {field.type === "BOOLEAN" ? "?" : ""}:{" "}
                                    {typeof field.value === "boolean" &&
                                    field.value !== undefined
                                      ? field.value
                                        ? "True"
                                        : "False"
                                      : field.value}
                                  </div>
                                ));
                              },
                            },
                          ]
                        : []),
                    ]}
                    rowActions={searchResult => {
                      const actions: RowAction<SearchResultWithContact>[] = [];

                      actions.push({
                        id: "details",
                        tooltipLabel: "Details",
                        icon: <DetailsIcon />,
                        onClick: () => setDialogOpenFor(searchResult),
                      });

                      return actions;
                    }}
                    actionSx={{ verticalAlign: "top" }}
                    rowsPerPage={pageSize}
                    page={page}
                    onChangeRowsPerPage={newSize => {
                      setPageSize(newSize);
                      setPage(0);
                      setCursorStack([]);
                    }}
                    onChangePage={handlePageChange}
                  />
                </FlexBox>
              );
            }}
          </AsyncWrapper>
        )}

        {dialogOpenFor && (
          <AudienceContactDialog
            contact={dialogOpenFor}
            onClose={() => {
              void refetch();
              setDialogOpenFor(null);
            }}
            fieldDefinitions={fieldDefinitions}
            audience={subscriberAudience}
          />
        )}
      </FlexBox>
    </>
  );
}
